import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useApiStable from "../../utils/useApiStable";
import "../../styles/LeagueCostSplit.css";

const fmtEur = (cents) =>
    (Number(cents || 0) / 100).toLocaleString("de-DE", {style: "currency", currency: "EUR"});

const fmtNum = (cents) =>
    (Number(cents || 0) / 100).toLocaleString("de-DE", {minimumFractionDigits: 2, maximumFractionDigits: 2});

const parseCents = (value) => {
    if (value === null || value === undefined || String(value).trim() === "") return NaN;
    const normalized = String(value).replace(/\s/g, "").replace(",", ".");
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : NaN;
};

/**
 * Verteilt die Summe aller Posten gleichmäßig auf alle Teilnehmer und schlägt
 * minimale Ausgleichszahlungen vor. Portierung von MatchCostCalculator (Backend),
 * damit die Anzeige ohne Roundtrip aktuell bleibt. Arbeitet auf participant.key.
 */
const calcSplit = (participants, expenses) => {
    const validExpenses = expenses
        .map(e => ({payerKey: e.payerKey, amountCents: parseCents(e.amount)}))
        .filter(e => Number.isFinite(e.amountCents) && e.amountCents > 0);
    const totalCents = validExpenses.reduce((sum, e) => sum + e.amountCents, 0);
    if (participants.length === 0) return {totalCents, shares: [], settlements: []};

    const sharingCount = participants.filter(p => !p.payerOnly).length;
    const base = sharingCount > 0 ? Math.floor(totalCents / sharingCount) : 0;
    const remainder = sharingCount > 0 ? totalCents - base * sharingCount : 0;
    const paid = {};
    participants.forEach(p => {
        paid[p.key] = 0;
    });
    validExpenses.forEach(e => {
        if (e.payerKey in paid) paid[e.payerKey] += e.amountCents;
    });

    let sharingRank = 0;
    const shares = participants.map(p => {
        let shareCents = 0;
        if (!p.payerOnly) {
            shareCents = base + (sharingRank < remainder ? 1 : 0);
            sharingRank += 1;
        }
        const paidCents = paid[p.key] || 0;
        return {key: p.key, paidCents, shareCents, balanceCents: paidCents - shareCents};
    });

    const debtors = shares.filter(s => s.balanceCents < 0).map(s => ({key: s.key, amt: -s.balanceCents}));
    const creditors = shares.filter(s => s.balanceCents > 0).map(s => ({key: s.key, amt: s.balanceCents}));
    debtors.sort((a, b) => b.amt - a.amt);
    creditors.sort((a, b) => b.amt - a.amt);

    const settlements = [];
    let di = 0;
    let ci = 0;
    while (di < debtors.length && ci < creditors.length) {
        const amount = Math.min(debtors[di].amt, creditors[ci].amt);
        if (amount > 0) settlements.push({fromKey: debtors[di].key, toKey: creditors[ci].key, amountCents: amount});
        debtors[di].amt -= amount;
        creditors[ci].amt -= amount;
        if (debtors[di].amt === 0) di += 1;
        if (creditors[ci].amt === 0) ci += 1;
    }
    return {totalCents, shares, settlements};
};

const MatchCostSplitEditor = ({teamId, matchId, players = [], onSummaryChange}) => {
    const api = useApiStable();
    const keyCounter = useRef(0);
    const nextKey = () => `new-${(keyCounter.current += 1)}`;

    const [participants, setParticipants] = useState([]); // {key, personUuid|null, displayName}
    const [expenses, setExpenses] = useState([]); // {payerKey, description, amount}
    const [serverSettlements, setServerSettlements] = useState([]); // from last load/save
    const [exists, setExists] = useState(false);
    const [newName, setNewName] = useState("");
    const [dirty, setDirty] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);

    const emitSummary = useCallback((dto) => {
        if (!onSummaryChange) return;
        const settlements = dto.settlements || [];
        onSummaryChange({
            matchId,
            exists: !!dto.exists,
            participantCount: (dto.participants || []).length,
            expenseCount: (dto.expenses || []).length,
            totalCents: dto.totalCents || 0,
            balanced: settlements.length > 0 && settlements.every(s => s.settled),
        });
    }, [matchId, onSummaryChange]);

    const applyDto = useCallback((dto) => {
        setParticipants((dto.participants || []).map(p => ({
            key: p.key,
            personUuid: p.personUuid || null,
            displayName: p.displayName,
            payerOnly: !!p.payerOnly,
        })));
        setExpenses((dto.expenses || []).map(e => ({
            payerKey: String(e.payerParticipantId),
            description: e.description,
            amount: (e.amountCents / 100).toFixed(2),
        })));
        setServerSettlements(dto.settlements || []);
        setExists(!!dto.exists);
        setDirty(false);
        emitSummary(dto);
    }, [emitSummary]);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const dto = await api.get(`/league/teams/${teamId}/matches/${matchId}/cost-split`);
            applyDto(dto);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [teamId, matchId, applyDto]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const markDirty = () => {
        setDirty(true);
        setSaved(false);
    };

    const nameByKey = useMemo(() => {
        const map = new Map();
        participants.forEach(p => map.set(p.key, p.displayName));
        return map;
    }, [participants]);

    const payerOnlyByKey = useMemo(() => {
        const map = new Map();
        participants.forEach(p => map.set(p.key, p.payerOnly));
        return map;
    }, [participants]);

    const live = useMemo(() => calcSplit(participants, expenses), [participants, expenses]);

    const rosterSelectedUuids = useMemo(
        () => new Set(participants.filter(p => p.personUuid).map(p => p.personUuid)),
        [participants],
    );

    const toggleRosterPlayer = (player) => {
        markDirty();
        setParticipants(prev => {
            if (prev.some(p => p.personUuid === player.uuid)) {
                return prev.filter(p => p.personUuid !== player.uuid);
            }
            return [...prev, {key: nextKey(), personUuid: player.uuid, displayName: player.name, payerOnly: false}];
        });
    };

    const addFreeTextParticipant = () => {
        const name = newName.trim();
        if (!name) return;
        markDirty();
        setParticipants(prev => [...prev, {key: nextKey(), personUuid: null, displayName: name, payerOnly: false}]);
        setNewName("");
    };

    const togglePayerOnly = (key) => {
        markDirty();
        setParticipants(prev => prev.map(p => (p.key === key ? {...p, payerOnly: !p.payerOnly} : p)));
    };

    const removeParticipant = (key) => {
        markDirty();
        setParticipants(prev => prev.filter(p => p.key !== key));
        setExpenses(prev => prev.map(e => (e.payerKey === key ? {...e, payerKey: ""} : e)));
    };

    const addExpense = () => {
        markDirty();
        setExpenses(prev => [...prev, {payerKey: participants[0]?.key || "", description: "", amount: ""}]);
    };

    const updateExpense = (index, field, value) => {
        markDirty();
        setExpenses(prev => prev.map((e, i) => (i === index ? {...e, [field]: value} : e)));
    };

    const removeExpense = (index) => {
        markDirty();
        setExpenses(prev => prev.filter((_, i) => i !== index));
    };

    const validationError = useMemo(() => {
        if (expenses.length > 0 && !participants.some(p => !p.payerOnly)) {
            return "Bitte mindestens einen Teilnehmer angeben, der an der Aufteilung beteiligt ist.";
        }
        for (const e of expenses) {
            if (!e.payerKey || !participants.some(p => p.key === e.payerKey)) return "Jeder Kostenposten braucht einen Zahler.";
            if (!e.description.trim()) return "Jeder Kostenposten braucht eine Beschreibung.";
            const cents = parseCents(e.amount);
            if (!Number.isFinite(cents) || cents <= 0) return "Jeder Betrag muss größer als 0 sein.";
        }
        return null;
    }, [expenses, participants]);

    const handleSave = async () => {
        if (validationError) {
            setError(validationError);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const request = {
                participants: participants.map(p => ({
                    key: p.key,
                    personUuid: p.personUuid,
                    displayName: p.displayName,
                    payerOnly: p.payerOnly,
                })),
                expenses: expenses.map(e => ({
                    payerKey: e.payerKey,
                    description: e.description.trim(),
                    amountCents: parseCents(e.amount),
                })),
            };
            const dto = await api.put(`/league/teams/${teamId}/matches/${matchId}/cost-split`, request);
            applyDto(dto);
            setSaved(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const resetSplit = async () => {
        if (!window.confirm("Diese Kostenaufteilung komplett löschen?")) return;
        setError(null);
        try {
            await api.delete(`/league/teams/${teamId}/matches/${matchId}/cost-split`);
            await loadData();
        } catch (err) {
            setError(err.message);
        }
    };

    const toggleSettled = async (settlement, settled) => {
        setError(null);
        try {
            const dto = await api.put(`/league/teams/${teamId}/matches/${matchId}/cost-split/settlements`, {
                fromParticipantId: Number(settlement.fromParticipantId),
                toParticipantId: Number(settlement.toParticipantId),
                settled,
            });
            applyDto(dto);
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) return <p className="lcs-editor__loading">Lade Kostenaufteilung…</p>;

    const hasPayerOnly = participants.some(p => p.payerOnly);

    return (
        <div className="lcs-editor">
            {error && <p className="lcs-error">{error}</p>}

            {/* Teilnehmer */}
            <section className="lcs-section">
                <div className="lcs-section__title">Teilnehmer</div>

                {players.length > 0 && (
                    <div className="lcs-chips">
                        {players.map(player => {
                            const selected = rosterSelectedUuids.has(player.uuid);
                            return (
                                <button
                                    key={player.uuid}
                                    type="button"
                                    onClick={() => toggleRosterPlayer(player)}
                                    className={`lcs-chip${selected ? " lcs-chip--on" : ""}`}
                                >
                                    {selected ? "✓ " : "+ "}{player.name}
                                </button>
                            );
                        })}
                    </div>
                )}

                <div className="lcs-people">
                    {participants.map(p => (
                        <div key={p.key} className="lcs-person">
                            <span className="lcs-person__name">
                                {p.displayName}
                                {!p.personUuid && <span className="tag"> · Gast</span>}
                            </span>
                            <label className="lcs-toggle">
                                <input
                                    type="checkbox"
                                    checked={p.payerOnly}
                                    onChange={() => togglePayerOnly(p.key)}
                                />
                                nur Zahler
                            </label>
                            <button
                                type="button"
                                onClick={() => removeParticipant(p.key)}
                                title="Teilnehmer entfernen"
                                className="lcs-iconbtn"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                    {participants.length === 0 && (
                        <span className="lcs-empty">Noch keine Teilnehmer.</span>
                    )}
                </div>

                {hasPayerOnly && (
                    <p className="lcs-hint">
                        „Nur Zahler" zahlen nur ein und werden nicht an der Aufteilung beteiligt.
                    </p>
                )}

                <div className="lcs-addrow">
                    <input
                        type="text"
                        className="lcs-field"
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                addFreeTextParticipant();
                            }
                        }}
                        placeholder="Name (Gast/Aushilfe)…"
                        maxLength={200}
                    />
                    <button type="button" className="lcs-btn" onClick={addFreeTextParticipant}>
                        + Name
                    </button>
                </div>
            </section>

            {/* Kostenposten */}
            <section className="lcs-section">
                <div className="lcs-section__title">Kostenposten</div>
                <div className="lcs-expenses">
                    {expenses.map((e, index) => (
                        <div key={index} className="lcs-expense">
                            <select
                                className="lcs-field lcs-expense__payer"
                                value={e.payerKey}
                                onChange={ev => updateExpense(index, "payerKey", ev.target.value)}
                            >
                                <option value="">— Zahler —</option>
                                {participants.map(p => (
                                    <option key={p.key} value={p.key}>{p.displayName}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                className="lcs-field lcs-expense__desc"
                                value={e.description}
                                onChange={ev => updateExpense(index, "description", ev.target.value)}
                                placeholder="Beschreibung"
                                maxLength={255}
                            />
                            <span className="lcs-money lcs-expense__amount">
                                <input
                                    type="text"
                                    className="lcs-field"
                                    inputMode="decimal"
                                    value={e.amount}
                                    onChange={ev => updateExpense(index, "amount", ev.target.value)}
                                    placeholder="0,00"
                                />
                            </span>
                            <button
                                type="button"
                                onClick={() => removeExpense(index)}
                                title="Posten entfernen"
                                className="lcs-iconbtn lcs-expense__del"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
                <button
                    type="button"
                    className="lcs-btn lcs-addexpense"
                    onClick={addExpense}
                    disabled={participants.length === 0}
                >
                    + Posten
                </button>
            </section>

            <div className="lcs-savebar">
                <button
                    type="button"
                    className="lcs-btn lcs-btn--primary"
                    onClick={handleSave}
                    disabled={saving || !!validationError}
                >
                    {saving ? "Speichern…" : "Speichern"}
                </button>
                {saved && !dirty && (
                    <span className="lcs-savebar__status lcs-savebar__status--ok">✓ Gespeichert</span>
                )}
                {dirty && (
                    <span className="lcs-savebar__status lcs-savebar__status--dirty">Nicht gespeicherte Änderungen</span>
                )}
                {validationError && (
                    <span className="lcs-savebar__status lcs-savebar__status--err">{validationError}</span>
                )}
                {exists && !dirty && (
                    <button type="button" className="lcs-linkbtn" onClick={resetSplit}>
                        Abrechnung löschen
                    </button>
                )}
            </div>

            {/* Ergebnis */}
            <section className="lcs-section">
                <div className="lcs-section__title">
                    Aufteilung <small>· Gesamt {fmtEur(live.totalCents)}</small>
                </div>
                {participants.length === 0 ? (
                    <span className="lcs-empty">Teilnehmer hinzufügen, um die Aufteilung zu sehen.</span>
                ) : (
                    <>
                        <div className="lcs-scroll">
                            <table className="lcs-table">
                                <thead>
                                <tr>
                                    <th>Teilnehmer</th>
                                    <th>bezahlt</th>
                                    <th>Anteil</th>
                                    <th>Saldo</th>
                                </tr>
                                </thead>
                                <tbody>
                                {live.shares.map(s => {
                                    const payerOnly = payerOnlyByKey.get(s.key);
                                    return (
                                        <tr key={s.key} className={payerOnly ? "lcs-row--payeronly" : undefined}>
                                            <td>
                                                {nameByKey.get(s.key) || "?"}
                                                {payerOnly && <span className="lcs-tag"> · nur Zahler</span>}
                                            </td>
                                            <td>{fmtNum(s.paidCents)}</td>
                                            <td>{fmtNum(s.shareCents)}</td>
                                            <td className={s.balanceCents < 0 ? "lcs-bal--neg" : "lcs-bal--pos"}>
                                                {s.balanceCents > 0 ? "+" : ""}{fmtNum(s.balanceCents)}
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                        <p className="lcs-hint">Beträge in €</p>
                    </>
                )}
            </section>

            {/* Ausgleichsvorschlag */}
            {(dirty ? live.settlements.length > 0 : serverSettlements.length > 0) && (
                <section className="lcs-section">
                    <div className="lcs-section__title">Ausgleichsvorschlag</div>
                    <div className="lcs-settle">
                        {dirty ? (
                            <>
                                {live.settlements.map((s, i) => (
                                    <div key={i} className="lcs-settle__row">
                                        <span>
                                            <strong>{nameByKey.get(s.fromKey) || "?"}</strong> zahlt{" "}
                                            <strong>{nameByKey.get(s.toKey) || "?"}</strong> {fmtEur(s.amountCents)}
                                        </span>
                                    </div>
                                ))}
                                <div className="lcs-settle__note">Zum Abhaken bitte zuerst speichern.</div>
                            </>
                        ) : (
                            serverSettlements.map((s, i) => (
                                <label
                                    key={i}
                                    className={`lcs-settle__row${s.settled ? " lcs-settle__row--done" : ""}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={s.settled}
                                        onChange={ev => toggleSettled(s, ev.target.checked)}
                                    />
                                    <span>
                                        <strong>{nameByKey.get(String(s.fromParticipantId)) || "?"}</strong> zahlt{" "}
                                        <strong>{nameByKey.get(String(s.toParticipantId)) || "?"}</strong> {fmtEur(s.amountCents)}
                                    </span>
                                </label>
                            ))
                        )}
                    </div>
                </section>
            )}
        </div>
    );
};

export default MatchCostSplitEditor;
